/**
 * Learn Session page — a conversation with the learn agent, ON the learn
 * board. Opening the agent shows the learning experience (due-for-review,
 * targets, curricula, recommendations, record) with the conversation docked
 * beneath it, exactly as the main app's Home detail pane does — the same
 * shared LearnBoardConnected, so the two can't drift (MentorSession's
 * precedent). Before this, the page was a bare transcript: a nurse who
 * tapped "Learn" landed full-screen on an empty message list and a composer,
 * with nothing to show what the agent could do.
 *
 * This page owns only the satellite chrome (leave-to-twin, back, title,
 * share); the board owns the reads, the chat side-channel and the writes.
 * There is no patient-frame/handover machinery here — a learn conversation
 * carries neither.
 *
 * The quiz itself never renders here — a training_step message folds to
 * nothing inline (UIComponentRenderer.tsx) and an training_handoff offer renders
 * as a button that spawns a CHILD session, handled by the /runs/:id route
 * (Run.tsx), not this page.
 */
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, Share2 } from 'lucide-react'
import { useSession } from '@frontend/hooks/useSessions'
import { useReturnToParent } from '@frontend/hooks/useSessionHandoff'
import { ReturnToParentButton } from '@frontend/components/session/ReturnToParentButton'
import { Skeleton } from '@frontend/components/ui/skeleton'
import ShareDialog from '@frontend/components/ShareDialog'
import { ReturnToMainAgent } from '@frontend/components/ReturnToMainAgent'
// The SAME face the main app mounts in-frame. Full-screen additionally passes
// this app's own navigation handlers; the frame has no such routes.
import Face from '../face'

export default function LearnSession() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const sessionId = id || ''

  const { data: session, isLoading, isError, refetch } = useSession(sessionId)
  const [shareOpen, setShareOpen] = useState(false)
  const { isChild, returnToParent } = useReturnToParent(session?.session.parent, sessionId)

  if (!id) return null

  if (isError && !session) {
    return (
      <div className="flex h-dvh flex-col">
        <header className="flex items-center gap-2 border-b border-neutral-200 bg-white px-4 py-3">
          <ReturnToMainAgent className="-ml-2" />
        </header>
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
          <p className="text-[15px] text-neutral-500">{t('session.loadError', 'Could not load this conversation.')}</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="min-h-11 rounded-full bg-primary px-5 text-[15px] font-semibold text-primary-foreground transition active:scale-95"
          >
            {t('actions.retry', 'Retry')}
          </button>
        </div>
      </div>
    )
  }

  if (isLoading && !session) {
    return (
      <div className="flex h-dvh flex-col">
        <header className="flex items-center gap-2 border-b border-neutral-200 bg-white px-4 py-3">
          <ReturnToMainAgent className="-ml-2" />
          <Skeleton className="h-6 w-40" />
        </header>
        <div className="flex flex-1 items-center justify-center">
          <div className="animate-pulse text-neutral-500">{t('learn.session.loading', 'Loading…')}</div>
        </div>
      </div>
    )
  }

  if (!session) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <p className="text-neutral-500">{t('session.notFound', 'Conversation not found')}</p>
      </div>
    )
  }

  return (
    <div className="flex h-dvh flex-col bg-neutral-50">
      <header className="flex items-center gap-1 border-b border-neutral-200 bg-white px-2 py-2 pt-[max(0.5rem,env(safe-area-inset-top))] sm:px-4 sm:py-3">
        <ReturnToMainAgent />
        <button
          onClick={() =>
            isChild
              ? returnToParent()
              : window.history.length > 1
                ? navigate(-1)
                : navigate('/')
          }
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-neutral-600 transition active:scale-95 hover:bg-neutral-100"
          aria-label={t('common.back', 'Back')}
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <h1 className="min-w-0 flex-1 truncate text-base font-semibold text-neutral-900">
          {session.session.title || t('learn.homeTitle', 'Learn')}
        </h1>
        <ReturnToParentButton parentSessionId={session.session.parent} childSessionId={sessionId} />
        <button
          onClick={() => setShareOpen(true)}
          aria-label={t('session.share', 'Share conversation')}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-primary transition active:scale-95 hover:bg-primary/10"
        >
          <Share2 className="h-[18px] w-[18px]" />
        </button>
      </header>

      <main className="min-h-0 flex-1">
        <Face session={session} />
      </main>

      <ShareDialog
        resourceId={sessionId}
        resourceTitle={session.session.title || t('learn.homeTitle', 'Learn')}
        resourceType="session"
        open={shareOpen}
        onOpenChange={setShareOpen}
      />
    </div>
  )
}
